import { useCallback } from 'react'
import { Button, Col, Input, Row, Select } from 'antd'
import { ClearOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { type ProductStatus, useProductStatusConfig } from './productStatusConfig'

const PRODUCT_STATUSES: ProductStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'SOLD_OUT']

interface ProductListFiltersProps {
  status: ProductStatus | ''
  name: string
  onStatusChange: (status: ProductStatus | '') => void
  onNameChange: (name: string) => void
  onReset: () => void
}

export function ProductListFilters({
  status,
  name,
  onStatusChange,
  onNameChange,
  onReset,
}: ProductListFiltersProps) {
  const { t } = useTranslation()
  const statusConfig = useProductStatusConfig()

  const statusOptions = [
    { value: '', label: t('pages.products.filters.allStatuses') },
    ...PRODUCT_STATUSES.map((s) => ({ value: s, label: statusConfig[s].label })),
  ]

  const hasActiveFilters = status !== '' || name !== ''

  const handleSearch = useCallback(
    (value: string) => {
      onNameChange(value)
    },
    [onNameChange],
  )

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} md={6}>
        <Select
          data-testid="product-list-filter-status"
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
          style={{ width: '100%' }}
          allowClear={false}
        />
      </Col>
      <Col xs={24} sm={12} md={8}>
        <Input.Search
          data-testid="product-list-filter-name"
          placeholder={t('pages.products.filters.searchPlaceholder')}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onSearch={handleSearch}
          allowClear
        />
      </Col>
      {hasActiveFilters && (
        <Col>
          <Button
            data-testid="product-list-filter-reset-btn"
            icon={<ClearOutlined />}
            onClick={onReset}
          >
            {t('pages.products.filters.resetFilters')}
          </Button>
        </Col>
      )}
    </Row>
  )
}
